import { PaginationProps } from '@components/V4/Pagination';
import Table, { SortOrder } from '@components/V4/Table/Table';
import Tooltip from '@components/V4/Tooltip';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { getCourseProgressPercentage, mergeClassNames } from 'apps/agora/src/utils/helpers';
import { COLORS } from 'apps/agora/src/v3/global';
import moment from 'moment';

interface ParentsSurveyTableProps {
  enrollments?: any;
  areEnrollmentsLoading: boolean;
  pagination: PaginationProps;
  sortBy: string | null;
  sortOrder: SortOrder | null;
  setSearchParams: (data: Partial<any>) => void;
  mentorModalOpenHandler: (enrollmentId: string) => void;
  enrollmentModalOpenHandler: (enrollmentId: string) => void;
  openStudentDetailsModalHandler: (enrollmentId: string) => void;
  surveyModalOpenHandler: (enrollmentId: string) => void;
}

const ParentsSurveyTable = (props: ParentsSurveyTableProps) => {
  const {
    enrollments,
    areEnrollmentsLoading,
    setSearchParams,
    pagination,
    sortBy,
    sortOrder,
    mentorModalOpenHandler,
    enrollmentModalOpenHandler,
    openStudentDetailsModalHandler,
    surveyModalOpenHandler,
  } = props;

  const sortHandler = (field: string, order: SortOrder) => {
    setSearchParams({
      sortBy: order ? field : null,
      sortOrder: order || null,
      page: 1,
    });
  };

  return areEnrollmentsLoading ? (
    <div className="h-24 flex items-center justify-center text-customGrey">Loading...</div>
  ) : (
    <section className="relative px-18 mb-20">
      <Table pagination={pagination} className="mt-1">
        <Table.Header>
          <Table.Row isHeader>
            <Table.HeaderCell>Course</Table.HeaderCell>
            <Table.HeaderCell>Student</Table.HeaderCell>
            <Table.HeaderCell>Mentor</Table.HeaderCell>
            <Table.HeaderCell>Parent</Table.HeaderCell>
            <Table.HeaderCell>Last Session</Table.HeaderCell>
            <Table.HeaderCell>Last Parent Feedback</Table.HeaderCell>
            <Table.HeaderCell>Star Rating</Table.HeaderCell>
            <Table.HeaderCell>Completion</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body hasResults={!areEnrollmentsLoading && !!enrollments?.length}>
          {enrollments?.map((enrollment: any) => (
            <Table.Row key={enrollment._id}>
              <Table.Cell
                onClick={() => enrollmentModalOpenHandler(enrollment._id)}
                className="cursor-pointer underline"
              >
                {enrollment.course?.title}
              </Table.Cell>

              <Table.Cell
                onClick={
                  enrollment.student?._id
                    ? () => openStudentDetailsModalHandler(enrollment.student._id)
                    : undefined
                }
              >
                <Tooltip text={enrollment.student?.isDeactivated ? 'Deactivated' : undefined}>
                  <span
                    className={mergeClassNames('flex gap-1', {
                      'line-through': enrollment.student?.isDeactivated,
                    })}
                  >
                    {enrollment.student?.name ?? 'n/a'}{' '}
                    {enrollment.student?.isDeactivated && (
                      <ExclamationCircleIcon className="w-4 h-4" aria-label="Deactivated user" />
                    )}
                  </span>
                </Tooltip>
              </Table.Cell>

              <Table.Cell
                onClick={
                  enrollment.mentor?._id
                    ? () => mentorModalOpenHandler(enrollment.mentor._id)
                    : undefined
                }
              >
                {enrollment.mentor?.name ?? 'n/a'}
              </Table.Cell>

              <Table.Cell>{enrollment.parent?.name ?? 'n/a'}</Table.Cell>

              <Table.Cell>
                {enrollment.lastCompletedSessionDate
                  ? moment(enrollment.lastCompletedSessionDate).format('DD.MM.YYYY')
                  : 'n/a'}
              </Table.Cell>

              <Table.Cell>
                {enrollment.lastParentFeedbackDate
                  ? moment(enrollment.lastParentFeedbackDate).format('DD.MM.YYYY')
                  : 'n/a'}
              </Table.Cell>

              <Table.Cell
                className={`${
                  enrollment.starRatingAverage !== undefined && enrollment.starRatingAverage <= 3
                    ? 'font-bold text-red-600'
                    : ''
                }`}
              >
                {enrollment.starRatingAverage?.toFixed(2) ?? 'n/a'}
              </Table.Cell>

              <Table.Cell>
                <Tooltip
                  placement="top"
                  text="Completed Sessions / Scheduled or Ongoing Sessions / Total No. of Sessions"
                >
                  <div className="flex flex-col gap-1">
                    <p>
                      {`${enrollment.noOfSessions.completed || 0} / ${
                        enrollment.noOfSessions.scheduledOrPending || 0
                      } / ${enrollment.noOfSessions.total || 0}`}
                    </p>
                    <div className="relative w-full h-2 rounded-full bg-customGrey bg-opacity-50 min-w-24">
                      <div
                        style={{
                          width: getCourseProgressPercentage(
                            enrollment.noOfSessions.completed,
                            enrollment.noOfSessions.total,
                            enrollment.noOfSessions.scheduledOrPending || 0
                          ),
                          background: `${enrollment?.course?.color ?? COLORS.PRIMARY}59`,
                        }}
                        className={mergeClassNames(`rounded-full h-full`)}
                      />
                      <div
                        style={{
                          width: getCourseProgressPercentage(
                            enrollment.noOfSessions.completed,
                            enrollment.noOfSessions.total
                          ),
                          background: enrollment?.course?.color ?? COLORS.PRIMARY,
                        }}
                        className={mergeClassNames(`absolute top-0 left-0 rounded-full h-full`)}
                      />
                    </div>
                  </div>
                </Tooltip>
              </Table.Cell>

              <Table.Cell>
                <div className="flex gap-2">
                  <span
                    className="cursor-pointer underline"
                    onClick={() => surveyModalOpenHandler(enrollment._id)}
                  >
                    Feedback
                  </span>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </section>
  );
};

export default ParentsSurveyTable;
